import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { isStipeProvider } from 'constants/provider';
import { getPaymentSession } from 'helpers/trustpayService';
import { useFeatureFlag } from 'hooks/useFeatureFlag';
import moment from 'moment';
import { useMappedState } from 'redux-react-hook';
import { errorAlert } from 'ui-kit/Alert';
import Spinner from 'ui-kit/Spinner';
import { isBlank } from 'utils/storeUtils';

import TrackingAPI from '../../../api/TrackingAPI';
import ChevronIcon from '../../../assets/button-chevron-left.svg?react';
import premium_icon from '../../../assets/Premium.svg';
import check_im from '../../../assets/premium_check.svg';
import { useGetCurrentPromotionMode } from '../../../config/promotions';
import CommonHelper from '../../../helpers/CommonHelper';
import StripeOfferBannerModal from '../../../modals/StripeOfferBannerModal';
import Modal from '../../../ui-kit/Modal';
import { formatDateToFullFormatWithSeconds, FULL_DATE_FORMAT_WITH_SECONDS, now } from '../../../utils/dates';
import { FEATURES, PAYMENTS_INFO, TRIAL_BUTTON_DATA } from './constants';
import { useTimer } from './hooks';
import {
  ButtonContainer,
  CloseIcon,
  Container,
  Feature,
  FeatureBlock,
  FeatureImage,
  GradientSpan,
  HeaderText,
  InfoContainer,
  InfoIcon,
  InfoRow,
  InfoText,
  MainText,
  ModalButton,
  ModalContent,
  ModalHeader,
  ModalWrapper,
  PremiumIcon,
  PromotionBlock,
  PromotionContent,
  PromotionLabel,
  PromotionStars,
  PromotionSubText,
  PromotionText,
  PromotionTimer,
  RightBox,
  RightContainer,
  RightText,
  SubText,
  TextColumn,
  TrialButton,
  UpgradeButton
} from './styles';

import './styles.css';

const SubscriptionModal = (props) => {
  const {
    isOpen,
    onClose,
    analyticsData = {},
    showScenarioButton = false,
    withBlurBackground = true,
  } = props;

  const navigate = useNavigate();

  const [isProcessing, setIsProcessing] = useState({});

  const isTrialPaywallButtonEnabledFeatureFlag = useFeatureFlag('trialPaywallButton');

  const { currentUser } = useMappedState(
    useCallback(
      state => ({
        currentUser: state.users.usersMap.get(state.users.currentUserId)
      }),
      []
    )
  );

  const mode = useGetCurrentPromotionMode();
  const timerTime = useTimer(mode.dateEnd || null);

  const paymentProviderUsed = currentUser?.subscription?.paymentProvider ?? '';
  const isStripeProvider = isStipeProvider(paymentProviderUsed);

  const subscriptionEndDate = formatDateToFullFormatWithSeconds(currentUser?.subscription?.currentPeriodEndDate ?? '');

  const expireSubscriptionDate = moment(subscriptionEndDate, FULL_DATE_FORMAT_WITH_SECONDS);
  const nowTime = now();
  const duration = moment.duration(expireSubscriptionDate.diff(nowTime));

  const daysToExpireSubscription = Math.floor(duration.asDays());
  const isExpireLessWeekSub = daysToExpireSubscription <= 6;
  const isExpiredSub = nowTime.isAfter(expireSubscriptionDate);

  const needToShowStripeOfferBanner = isStripeProvider && (isExpireLessWeekSub || isExpiredSub) && currentUser?.paymentProvider !== 'stripe4';

  const handleBuyPremium = async (plan) => {
    if (!currentUser) {
      errorAlert('Please sign in to upgrade');
      return;
    }
    try {
      setIsProcessing(prev => ({ ...prev, [plan]: true }));
      await getPaymentSession(plan);
      setTimeout(() => {
        setIsProcessing({});
      }, 3500);
    } catch (error) {
      setIsProcessing({});
      throw error;
    }
  };

  const handleScenarioButtonClick = () => {
    navigate('/?#featured-scenarios');
  };

  useEffect(() => {
    if (isOpen) {
      // DialogsAPI.getLazyQuestParams().then(abData => {
      //   setPaymentProviderToUse(abData?.payment_provider);
      // });
      if (!needToShowStripeOfferBanner) {
        TrackingAPI.trackEvent('subscription_popup_rendered', analyticsData);
        CommonHelper.yandexTrackEvent(`subscription-modal-opened`);
      }
    }
  }, [isOpen, needToShowStripeOfferBanner]);

  if (!currentUser || currentUser?.hasSubscription) {
    return null;
  }

  if (needToShowStripeOfferBanner && isOpen) {
    return <StripeOfferBannerModal isOpen={isOpen} onClose={onClose} analyticsDataRenderReason={analyticsData?.rendered_for_reason ?? ''} />;
  }



  const SubscriptionButton = ({ buttonType, buttonData, handleClick }) => {
    const ButtonComponent = {
      year: UpgradeButton,
      halfYear: UpgradeButton,
      month: ModalButton,
      trial: TrialButton
    }[buttonType];

    const isVisibleChevron = buttonType === 'year' || buttonType === 'halfYear';
    return (
      <ButtonComponent
        onClick={handleClick}
        disabled={!isBlank(isProcessing)}
      >
        <ButtonContainer>
          <TextColumn>
            <MainText>{buttonData.title}</MainText>
            <SubText>{buttonData.subText}</SubText>
          </TextColumn>
          <RightContainer>
            {isVisibleChevron && (
              <RightBox>
                <RightText>{buttonData.chevronText}</RightText>
              </RightBox>
            )}
            {isProcessing[buttonType] ? <Spinner size={20} /> : buttonType !== 'trial' && <ChevronIcon style={{ height: '100%' }} />}
          </RightContainer>
        </ButtonContainer>
      </ButtonComponent>
    );
  };

  return (
    <Modal isOpen={isOpen} withBlurBackground={withBlurBackground} isVisibleCloseIcon={false} canClose onClose={onClose} className="upgrade-modal">
      <Container>
        <CloseIcon onClick={onClose} />
        <ModalWrapper>
          <ModalContent>
            <ModalHeader>
              <PremiumIcon src={premium_icon} alt="Premium" />
              <HeaderText>
                Upgrade to <GradientSpan>Premium</GradientSpan>
              </HeaderText>
            </ModalHeader>

            {mode?.upperBlock && (
              <PromotionBlock>
                <PromotionStars />
                <PromotionContent>
                  <PromotionText>
                    <PromotionLabel>{mode.upperBlock.title}</PromotionLabel>
                    <PromotionSubText>{mode.upperBlock.subText}</PromotionSubText>
                  </PromotionText>
                  <PromotionTimer>
                    {`${timerTime.hours} : ${timerTime.minutes} : ${timerTime.seconds}`}
                  </PromotionTimer>
                </PromotionContent>
              </PromotionBlock>
            )}

            <FeatureBlock>
              {FEATURES.map(feature => (
                <Feature key={feature}>
                  <FeatureImage src={check_im} alt="check" />
                  <span>{feature}</span>
                </Feature>
              ))}
            </FeatureBlock>
            {mode?.buttonBlock && (
              <>
                <SubscriptionButton buttonType="year" buttonData={mode.buttonBlock.yearButton} handleClick={() => handleBuyPremium('year')} />
                <SubscriptionButton buttonType="month" buttonData={mode.buttonBlock.monthButton} handleClick={() => handleBuyPremium('month')} />
                {isTrialPaywallButtonEnabledFeatureFlag && (
                  <SubscriptionButton buttonType="month" buttonData={mode.buttonBlock.trialTestButton} handleClick={() => handleBuyPremium('trialTest')} />
                )}
              </>
            )}
            {showScenarioButton && (
              <SubscriptionButton
                handleClick={handleScenarioButtonClick}
                buttonType="trial"
                buttonData={TRIAL_BUTTON_DATA}
              />
            )}

            <InfoContainer>
              {PAYMENTS_INFO.map((info, i) => (
                <InfoRow key={`info_${i}`}>
                  <InfoIcon src={info.icon} alt="info" />
                  <InfoText>{info.label}</InfoText>
                </InfoRow>
              ))}
            </InfoContainer>
          </ModalContent>
        </ModalWrapper>
      </Container>
    </Modal>
  );
};

export default SubscriptionModal;
